import SecureRequest from "../utils/securerequest";
import { buildUrl } from "../utils/buildUrl";

/**
 * @typedef {Object} ProviderClinicLink
 * @property {string} id
 * @property {string} clinic_id
 * @property {{id: string, clinic_name: string}} clinic
 */

/**
 * @typedef {Object} ProviderRecord
 * @property {string} id
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} email
 * @property {string} [fax_number]
 * @property {string} [npi_number]
 * @property {string} [medicaid_id]
 * @property {string} phone
 * @property {string} [specialty]
 * @property {boolean} accepts_insurance
 * @property {string} physical_address
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [zip_code]
 * @property {string} [website]
 * @property {ProviderClinicLink[]} physician_clinic
 */

/**
 * @typedef {Object} ProviderFilterOption
 * @property {string} id
 * @property {string} display_name
 * @property {number} count
 */

/**
 * @typedef {Object} ProvidersListResponse
 * @property {ProviderRecord[]} providers
 * @property {number} count
 * @property {ProviderFilterOption[]} accepted_insurance
 * @property {ProviderFilterOption[]} clinics
 * @property {ProviderFilterOption[]} providers_filter
 */

/**
 * @param {Object} [params] - Query params for filtering/pagination
 * @returns {Promise<{data: ProvidersListResponse}>} The promise object of the API request
 */
export const getAllProviders = async (params = {}) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();

  return await Request.get(buildUrl(`/v3/provider`, params), {});
};

/**
 * Gets a provider by its ID.
 *
 * @param {String} id - The ID of the provider to retrieve.
 * @returns {Promise<Object>} The promise object of the API request.
 */
export const getProviderById = async (id) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  const url = `/v3/provider/${id}`;
  return await Request.get(url);
};

/**
 * Creates a new provider.
 *
 * @param {Object} payload - The data for the new provider.
 * @returns {Promise<Object>} The promise object of the API request.
 */
export const createProvider = async (payload) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return Request.post(`/v3/provider`, payload);
};

/**
 * Updates an existing provider.
 *
 * @param {String} providerId - The ID of the provider to update.
 * @param {Object} payload - The data to update the provider with.
 * @returns {Promise<Object>} The promise object of the API request.
 */
export const updateProvider = async (providerId, payload) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return Request.patch(`/v3/provider/${providerId}`, payload);
};

export const searchProviders = async (query) => {
  const sr = new SecureRequest();
  const Request = sr.getRequest();
  return await Request.get(buildUrl(`/v3/provider/search`, query));
};
